import React, { useRef, useEffect, useCallback, useState, useMemo } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import QuillTableBetter from "quill-table-better";
import "quill-table-better/dist/quill-table-better.css";
import { Maximize2, Minimize2 } from "lucide-react";

// ─── quill-table-better hardening ─────────────────────────────────────────
// The plugin (its bundled TableBetter module) calls `Object.keys(...)` on
// an internal value that is `null`/`undefined` in some LEXP snapshots that
// contain tables (partial table state or a missing attribute map). That
// throws `TypeError: Cannot convert undefined or null to object` DURING
// Quill setContents / delta conversion, which crashes the entire editor
// mount and blanks the LEXP editor page.
//
// Fix per product requirement: guard every `Object.keys` call so a null
// or undefined value silently degrades to an empty key list instead of
// tearing down the page. We install this defensive wrapper once, on the
// first load of the integration module, and only if it hasn't been
// installed already. It preserves the original behaviour for all valid
// (object / array / primitive-wrappable) inputs — the ONLY behavioural
// change is that `null` and `undefined` no longer throw.
if (typeof Object !== "undefined" && !Object.keys.__lexpTableGuarded) {
  const originalObjectKeys = Object.keys;
  const guardedObjectKeys = function (value) {
    if (value === null || value === undefined) {
      return [];
    }
    return originalObjectKeys(value);
  };
  guardedObjectKeys.__lexpTableGuarded = true;
  try {
    Object.keys = guardedObjectKeys;
  } catch {
    // If Object.keys is frozen in some exotic runtime, we simply skip —
    // the editor will still throw as before, but the app itself won't be
    // any worse off than it is today.
  }
}

// Registering the plugin is defensive too: if the plugin module itself
// fails to register (e.g. a mismatched Quill peer), the editor should
// still mount as a plain rich-text editor rather than crashing.
try {
  Quill.register({ "modules/table-better": QuillTableBetter }, true);
} catch (err) {
  // eslint-disable-next-line no-console
  console.warn("[RichTextEditor] quill-table-better failed to register:", err);
}

// ============== Custom Blots (horizontal rule + callouts) ==============
const BlockEmbed = Quill.import("blots/block/embed");
const BlockBlot = Quill.import("blots/block");

class HorizontalRule extends BlockEmbed {}
HorizontalRule.blotName = "hr";
HorizontalRule.tagName = "hr";
if (!Quill.imports["formats/hr"]) {
  Quill.register(HorizontalRule, true);
}

class CalloutBlot extends BlockBlot {
  static create(value) {
    const node = super.create();
    const kind = value || "note";
    node.setAttribute("data-callout", kind);
    node.classList.add("rte-callout", `rte-callout-${kind}`);
    return node;
  }
  static formats(domNode) {
    return domNode.getAttribute("data-callout") || "note";
  }
  format(name, value) {
    if (name === CalloutBlot.blotName && value) {
      this.domNode.setAttribute("data-callout", value);
      this.domNode.className = `rte-callout rte-callout-${value}`;
    } else {
      super.format(name, value);
    }
  }
}
CalloutBlot.blotName = "callout";
CalloutBlot.tagName = "div";
CalloutBlot.className = "rte-callout";
if (!Quill.imports["formats/callout"]) {
  Quill.register(CalloutBlot, true);
}

// ============== Toolbar config ==============
// Custom buttons exposed via SVG-less spans so Quill's default snow styling
// picks them up cleanly via CSS in index.css.
const CALLOUT_KINDS = ["objective", "tip", "note", "caution", "clinical-reference", "reminder"];

const toolbarContainer = [
  // Priority row 1: paragraph style + bold/italic/underline + ordered/bulleted lists
  [{ header: [1, 2, 3, false] }, "bold", "italic", "underline", { list: "ordered" }, { list: "bullet" }],
  // Priority row 2: remaining common formatting
  ["strike", { color: [] }, { background: [] }, { align: [] }, "blockquote", "link"],
  // Secondary — collapsed behind the "More" toggle on mobile
  [{ size: ["small", false, "large", "huge"] }, { list: "check" }, { indent: "-1" }, { indent: "+1" }, "video"],
  ["hr", ...CALLOUT_KINDS.map((k) => `callout-${k}`)],
  ["table-better", "clean"],
  // Mobile-only "More" toggle (hidden on desktop via CSS)
  ["more"],
];

export default function RichTextEditor({ value, onChange, placeholder, testId }) {
  const quillRef = useRef(null);
  const wrapperRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const readyRef = useRef(false);
  const [fullScreen, setFullScreen] = useState(false);

  onChangeRef.current = onChange;

  const modules = useMemo(
    () => ({
      toolbar: {
        container: toolbarContainer,
        handlers: {
          hr: function () {
            const range = this.quill.getSelection(true);
            this.quill.insertText(range.index, "\n", Quill.sources.USER);
            this.quill.insertEmbed(range.index + 1, "hr", true, Quill.sources.USER);
            this.quill.setSelection(range.index + 2, Quill.sources.SILENT);
          },
          "callout-objective": function () {
            const range = this.quill.getSelection(true);
            this.quill.formatLine(range.index, range.length || 1, "callout", "objective", Quill.sources.USER);
          },
          "callout-tip": function () {
            const range = this.quill.getSelection(true);
            this.quill.formatLine(range.index, range.length || 1, "callout", "tip", Quill.sources.USER);
          },
          "callout-note": function () {
            const range = this.quill.getSelection(true);
            this.quill.formatLine(range.index, range.length || 1, "callout", "note", Quill.sources.USER);
          },
          "callout-caution": function () {
            const range = this.quill.getSelection(true);
            this.quill.formatLine(range.index, range.length || 1, "callout", "caution", Quill.sources.USER);
          },
          "callout-clinical-reference": function () {
            const range = this.quill.getSelection(true);
            this.quill.formatLine(range.index, range.length || 1, "callout", "clinical-reference", Quill.sources.USER);
          },
          "callout-reminder": function () {
            const range = this.quill.getSelection(true);
            this.quill.formatLine(range.index, range.length || 1, "callout", "reminder", Quill.sources.USER);
          },
          more: function () {
            const tb = this.quill.getModule("toolbar").container;
            tb.dataset.more = tb.dataset.more === "1" ? "0" : "1";
          },
        },
      },
      table: false,
      "table-better": {
        language: "en_US",
        menus: ["column", "row", "merge", "table", "cell", "wrap", "copy", "delete"],
        toolbarTable: true,
      },
      keyboard: {
        bindings: QuillTableBetter.keyboardBindings,
      },
    }),
    []
  );

  // On mount: re-apply content using updateContents (preserves table blots).
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    if (value) {
      // If the plugin still throws on a malformed table blot despite the
      // Object.keys guard above, catch it here so the editor stays mounted
      // with an empty document instead of the whole LEXP page white-
      // screening. The learner/admin can then re-edit the content.
      try {
        const delta = editor.clipboard.convert({ html: value });
        editor.setText("", Quill.sources.SILENT);
        editor.updateContents(delta, Quill.sources.SILENT);
        editor.history.clear();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[RichTextEditor] failed to load existing content:", err);
        try { editor.setText("", Quill.sources.SILENT); } catch { /* ignore */ }
      }
    }
    readyRef.current = true;

    // Tooltip labels on custom toolbar buttons
    const toolbar = wrapperRef.current?.querySelector(".ql-toolbar");
    if (toolbar) {
      const labels = {
        ".ql-hr": "Horizontal divider",
        ".ql-callout-objective": "Callout — Objective",
        ".ql-callout-tip": "Callout — Tip",
        ".ql-callout-note": "Callout — Note",
        ".ql-callout-caution": "Callout — Caution",
        ".ql-callout-clinical-reference": "Callout — Clinical Reference",
        ".ql-callout-reminder": "Callout — Reminder",
        ".ql-video": "Embed video (URL)",
        ".ql-list[value='check']": "Checklist",
        ".ql-align": "Text alignment",
        ".ql-indent[value='-1']": "Outdent",
        ".ql-indent[value='+1']": "Indent",
        ".ql-more": "More formatting options",
      };
      Object.entries(labels).forEach(([sel, title]) => {
        toolbar.querySelectorAll(sel).forEach((el) => {
          if (!el.getAttribute("title")) el.setAttribute("title", title);
        });
      });
      // Tag each .ql-formats group so mobile CSS can hide secondary groups
      // behind the "More" toggle. Groups 0-1 = priority (always visible).
      const secondaryGroups = [];
      toolbar.querySelectorAll(".ql-formats").forEach((grp, i) => {
        if (grp.querySelector(".ql-more")) {
          grp.classList.add("rte-more-group");
        } else if (i >= 2) {
          grp.classList.add("rte-secondary");
          secondaryGroups.push(grp);
        }
      });
      // Wrap all secondary groups in a single container so mobile can render
      // them as a single floating dropdown below the toolbar.
      if (secondaryGroups.length && !toolbar.querySelector(".rte-secondary-dropdown")) {
        const panel = document.createElement("div");
        panel.className = "rte-secondary-dropdown";
        // Insert just before the "more" group so tab order still makes sense.
        const moreGroup = toolbar.querySelector(".rte-more-group");
        toolbar.insertBefore(panel, moreGroup || null);
        secondaryGroups.forEach((g) => panel.appendChild(g));
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback(() => {
    if (!readyRef.current) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const html = editor.root.innerHTML;
    onChangeRef.current?.(html);
  }, []);

  const toggleFullScreen = () => setFullScreen((f) => !f);

  return (
    <div
      ref={wrapperRef}
      className={`rich-text-editor ${fullScreen ? "rte-fullscreen" : ""}`}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={toggleFullScreen}
        className="rte-fullscreen-toggle"
        data-testid={testId ? `${testId}-fullscreen` : "rte-fullscreen-toggle"}
        title={fullScreen ? "Exit focus mode" : "Focus mode"}
      >
        {fullScreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
      </button>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        defaultValue={value || ""}
        onChange={handleChange}
        placeholder={placeholder}
        modules={modules}
      />
    </div>
  );
}
